import React from 'react';
import { ClerkProvider, SignedIn, SignedOut, useUser } from '@clerk/clerk-react';
import { Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { GlobalStyles } from './styles/GlobalStyles';
import { ThemeProvider } from 'styled-components';
import { theme } from './styles/theme';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/layout/ScrollToTop';

// Pages
import Home from './pages/Home';
import Marcella from './pages/Marcella';
import Kurse from './pages/Kurse';
import Team from './pages/Team';
import Ponys from './pages/Ponys';
import Institutionen from './pages/Institutionen';
import Wochenplan from './pages/Tagesablauf';

// Institutionen Pages
import RegularSupport from './pages/institutionen/RegularSupport';
import MovingSchool from './pages/institutionen/MovingSchool';

// Course Pages
import Ponyclub from './pages/courses/Ponyclub';
import MukiClub from './pages/courses/MukiClub';
import HeilpaedagogischesReiten from './pages/courses/HeilpaedagogischesReiten';
import DynamicCourse from './pages/courses/DynamicCourse';
import QuickCheckout from './pages/courses/QuickCheckout';

// Success/Cancel Pages
import Success from './pages/Success';
import Cancel from './pages/Cancel';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminAccessDenied from './pages/admin/AdminAccessDenied';
import AdminLogin from './pages/admin/AdminLogin';
import AdminSignUp from './pages/admin/AdminSignUp';

// 404 Page
import NotFound from './pages/NotFound';
// Legal Pages
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import AGB from './pages/AGB';
import ComingSoon from './pages/ComingSoon';

// Clerk Publishable Key - MUSS aus Umgebungsvariablen kommen
// WICHTIG: Diese Variable wird beim Build-Zeitpunkt eingebettet!
// Create React App verwendet REACT_APP_* Präfix (nicht VITE_* oder NEXT_PUBLIC_*)
// Fallback: Akzeptiere auch VITE_* für Kompatibilität
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY || process.env.VITE_CLERK_PUBLISHABLE_KEY;

// Debug: Zeige Clerk-Konfiguration (Development und Production)
if (typeof window !== 'undefined') {
  console.log('🔑 Clerk Key vorhanden:', !!clerkPubKey);
  console.log('🔑 Key Quelle:', process.env.REACT_APP_CLERK_PUBLISHABLE_KEY ? 'REACT_APP_' : (process.env.VITE_CLERK_PUBLISHABLE_KEY ? 'VITE_' : 'NICHT GEFUNDEN'));
  console.log('🌐 Aktuelle Domain:', window.location.origin);
  console.log('🌐 Hostname:', window.location.hostname);
  console.log('🌐 NODE_ENV:', process.env.NODE_ENV);
  if (!clerkPubKey) {
    console.error('❌ CRITICAL: Clerk Key fehlt! Admin-Dashboard wird nicht funktionieren!');
  }
}

if (!clerkPubKey) {
  console.error('⚠️ Clerk Publishable Key ist nicht gesetzt!');
  console.error('⚠️ Erwartet: REACT_APP_CLERK_PUBLISHABLE_KEY (Create React App verwendet REACT_APP_* Präfix)');
  console.error('⚠️ Gefunden: VITE_CLERK_PUBLISHABLE_KEY (Vite Präfix funktioniert hier nicht!)');
  console.error('⚠️ Bitte in .env Datei umbenennen zu REACT_APP_CLERK_PUBLISHABLE_KEY');
  console.error('⚠️ Dann Build neu erstellen: npm run build');
}

// Admin Route Protection Component
const AdminRoute = ({ children }) => {
  const { user } = useUser();
  
  // Prüfe ob User Admin-Rolle hat
  const isAdmin = user?.publicMetadata?.role === 'admin';
  
  if (!isAdmin) {
    return <AdminAccessDenied />;
  }
  
  return children;
};

function App() {
  // Wenn kein Clerk Key gesetzt ist, rendere App ohne ClerkProvider
  if (!clerkPubKey) {
    return (
      <ThemeProvider theme={theme}>
        <Helmet>
          {/* Favicon - Logo */}
          <link rel="icon" type="image/webp" href="/assets/logo.webp" />
          <link rel="icon" type="image/webp" sizes="32x32" href="/assets/logo.webp" />
          <link rel="icon" type="image/webp" sizes="16x16" href="/assets/logo.webp" />
          <link rel="shortcut icon" type="image/webp" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="180x180" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="152x152" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="144x144" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="120x120" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="114x114" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="76x76" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="72x72" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="60x60" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="57x57" href="/assets/logo.webp" />
        </Helmet>
        <GlobalStyles />
        <ScrollToTop />
        <div className="App">
          <Navbar />
          <main>
            <Routes>
              {/* Hauptseiten */}
              <Route path="/" element={<Home />} />
              <Route path="/marcella" element={<Marcella />} />
              <Route path="/kurse" element={<Kurse />} />
              <Route path="/team" element={<Team />} />
              <Route path="/ponys" element={<Ponys />} />
              <Route path="/institutionen" element={<Institutionen />} />
              <Route path="/institutionen/regular-support" element={<RegularSupport />} />
              <Route path="/institutionen/moving-school" element={<MovingSchool />} />
              <Route path="/wochenplan" element={<Wochenplan />} />
              
              {/* Rechtliches */}
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/agb" element={<AGB />} />
              
              {/* Coming Soon */}
              <Route path="/kundenportal" element={<ComingSoon />} />
              
              {/* Kursseiten */}
              <Route path="/kurse/ponyclub" element={<Ponyclub />} />
              <Route path="/kurse/muki-club" element={<MukiClub />} />
              <Route path="/kurse/heilpaedagogisches-reiten" element={<HeilpaedagogischesReiten />} />
              <Route path="/kurse/buchen/:slug" element={<DynamicCourse />} />
              <Route path="/kurse/buchen/:slug/jetzt" element={<QuickCheckout />} />
              
              {/* Stripe Erfolgs-/Abbruch-Seiten */}
              <Route path="/success" element={<Success />} />
              <Route path="/cancel" element={<Cancel />} />
              
              {/* Admin Routes - nicht verfügbar ohne Clerk */}
              <Route path="/admin" element={<div style={{ padding: '2rem', textAlign: 'center' }}><h1>Admin-Bereich nicht verfügbar</h1><p>REACT_APP_CLERK_PUBLISHABLE_KEY ist nicht konfiguriert.</p></div>} />
              <Route path="/admin/sign-up" element={<div style={{ padding: '2rem', textAlign: 'center' }}><h1>Admin-Bereich nicht verfügbar</h1><p>REACT_APP_CLERK_PUBLISHABLE_KEY ist nicht konfiguriert.</p></div>} />
              
              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  // Clerk Domain-Konfiguration für verschiedene Ports
  // Wenn die App auf einem anderen Port läuft als erwartet, kann hier die Domain gesetzt werden
  // Standard: Clerk erkennt die Domain automatisch
  const clerkDomain = typeof window !== 'undefined' ? window.location.origin : undefined;
  
  // Debug: Zeige Clerk-Konfiguration in Production
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'production') {
    console.log('🔧 Clerk Provider Konfiguration:');
    console.log('  - Domain:', clerkDomain);
    console.log('  - Publishable Key vorhanden:', !!clerkPubKey);
    console.log('  - Hostname:', window.location.hostname);
  }
  
  return (
    <ClerkProvider 
      publishableKey={clerkPubKey}
      domain={clerkDomain}
    >
      <ThemeProvider theme={theme}>
        <Helmet>
          {/* Favicon - Logo */}
          <link rel="icon" type="image/webp" href="/assets/logo.webp" />
          <link rel="icon" type="image/webp" sizes="32x32" href="/assets/logo.webp" />
          <link rel="icon" type="image/webp" sizes="16x16" href="/assets/logo.webp" />
          <link rel="shortcut icon" type="image/webp" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="180x180" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="152x152" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="144x144" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="120x120" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="114x114" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="76x76" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="72x72" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="60x60" href="/assets/logo.webp" />
          <link rel="apple-touch-icon" sizes="57x57" href="/assets/logo.webp" />
        </Helmet>
        <GlobalStyles />
        <ScrollToTop />
        <div className="App">
          <Navbar />
          <main>
            <Routes>
              {/* Hauptseiten */}
              <Route path="/" element={<Home />} />
              <Route path="/marcella" element={<Marcella />} />
              <Route path="/kurse" element={<Kurse />} />
              <Route path="/team" element={<Team />} />
              <Route path="/ponys" element={<Ponys />} />
              <Route path="/institutionen" element={<Institutionen />} />
              <Route path="/institutionen/regular-support" element={<RegularSupport />} />
              <Route path="/institutionen/moving-school" element={<MovingSchool />} />
              <Route path="/wochenplan" element={<Wochenplan />} />
              
              {/* Rechtliches */}
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/datenschutz" element={<Datenschutz />} />
              <Route path="/agb" element={<AGB />} />
              
              {/* Coming Soon */}
              <Route path="/kundenportal" element={<ComingSoon />} />
              
              {/* Kursseiten */}
              <Route path="/kurse/ponyclub" element={<Ponyclub />} />
              <Route path="/kurse/muki-club" element={<MukiClub />} />
              <Route path="/kurse/heilpaedagogisches-reiten" element={<HeilpaedagogischesReiten />} />
              <Route path="/kurse/buchen/:slug" element={<DynamicCourse />} />
              <Route path="/kurse/buchen/:slug/jetzt" element={<QuickCheckout />} />
              
              {/* Stripe Erfolgs-/Abbruch-Seiten */}
              <Route path="/success" element={<Success />} />
              <Route path="/cancel" element={<Cancel />} />
              
              {/* Admin Routes */}
              <Route 
                path="/admin" 
                element={
                  <>
                    <SignedOut>
                      <AdminLogin />
                    </SignedOut>
                    <SignedIn>
                      <AdminRoute>
                        <AdminDashboard />
                      </AdminRoute>
                    </SignedIn>
                  </>
                } 
              />
              <Route path="/admin/sign-up" element={<AdminSignUp />} />
              
              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </div>
      </ThemeProvider>
    </ClerkProvider>
  );
}

export default App;